import debounce from 'lodash/debounce';
import './scrollTrigger';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getBreakpoint, horizontalResizeEvent } from './_utils';

const baseClass = 'FilteredColumnsList';
const activeFilterClass = `${baseClass}-filter--active`;
const activeDisplayButtonClass = `${baseClass}-displayButtons-button--active`;
const columnsItemClass = `${baseClass}-item--columns`;
const listItemClass = `${baseClass}-item--list`;
const columnsGridClasses = 'col-lg-6 col-xl-4';
const listGridClasses = 'col-lg-12 col-xl-8';
const gridOffsetClasses = 'offset-xl-2';
const selectors = {
	base: `.${baseClass}`,
	tabBar: `.${baseClass}-tabBar`,
	filter: `.${baseClass}-filter`,
	filtersContainer: `.${baseClass}-filtersContainer`,
	viewAllFilter: `.${baseClass}-filter--viewAll`,
	viewAllContainer: `.${baseClass}-viewAllContainer`,
	filterSection: `.${baseClass}-filterSection`,
	columnsButton: `.${baseClass}-displayButtons--columns`,
	listButton: `.${baseClass}-displayButtons--list`,
	item: `.${baseClass}-item`,
	stickyNav: `.${baseClass}-stickyNav`,
	dropdown: `.${baseClass}-stickyNav select`,
	stickyNavLine: `.${baseClass}-stickyNav-GrayLine`,
	desktopNav: '.Navigation-bar',
	mobileNav: '.Navigation-sm',
};

const inittabBar = $container => {
	//All the items we need
	const $tabBar = $container.find(selectors.tabBar);
	const $filters = $container.find(selectors.filter);
	const filterValues = $filters.map((index, filter) => $(filter).data('filterTag')).toArray();
	const $filtersContainer = $container.find(selectors.filtersContainer);
	const $viewAllContainer = $container.find(selectors.viewAllContainer);
	const $filterSections = $container.find(selectors.filterSection);
	const $columnsButton = $container.find(selectors.columnsButton);
	const $listButton = $container.find(selectors.listButton);
	const $allItems = $container.find(selectors.item);
	const $stickyNav = $container.find(selectors.stickyNav);
	const $dropdown = $container.find(selectors.dropdown);
	const $stickyNavLine = $stickyNav.find(selectors.stickyNavLine);
	const mobileNavHeight = $(selectors.mobileNav).height();
	const desktopNavHeight = $(selectors.desktopNav).outerHeight();
	let topOfContainer = $container.offset().top;
	let hashParam = window.location.hash.replace('#', '');
	let defaultFilterTag = $container.data('defaultFilter');
	let activeFilterTag = 'all';
	let usingDefaultFilter = false;

	if (filterValues.includes(hashParam)) {
		//if we have a hash param on load, we'll scroll to the component. This prevents the browser from jumping back to a different scroll position (not supported in IE)
		window.history.scrollRestoration = 'manual';
		activeFilterTag = hashParam;
	} else if (filterValues.includes(defaultFilterTag)) {
		usingDefaultFilter = true;
		activeFilterTag = defaultFilterTag;
		window.history.scrollRestoration = 'auto';
	} else {
		window.history.scrollRestoration = 'auto';
	}

	//Sticky Tablet/Mobile
	gsap.to($dropdown, {
		scrollTrigger: {
			trigger: $stickyNav,
			endTrigger: $container,
			start: `top-=${mobileNavHeight} top`,
			end: `bottom top+=${mobileNavHeight + $stickyNav.height()}`,
			pin: true,
			pinSpacing: false,
			scrub: true,
		},
		ease: 'none',
	});

	//Layout Fixes
	const checkFiltersContainer = () => {
		if ($filtersContainer.height() > $viewAllContainer.height()) {
			$tabBar.css('align-items', 'flex-start');
		} else {
			$tabBar.css('align-items', 'center');
		}
	};
	checkFiltersContainer();

	//Fix issue with bolded text moving stuff around
	const fixFilterBold = () => {
		$filters.addClass(activeFilterClass);
		$filters.each((index, filter) => {
			const $filter = $(filter);
			$filter.css('min-width', $filter.width());
			$filter.removeClass(activeFilterClass);
		});
		$container
			.find(`${selectors.filter}[data-filter-tag='${activeFilterTag}']`)
			.addClass(activeFilterClass);
	};
	fixFilterBold();

	//Fix Sticky Nav Gray Line
	const fixStickyNav = () => {
		$stickyNavLine.css(
			'left',
			`calc((100% - 100vw ) / 2 - ${Math.ceil(
				($(window).outerWidth() - $(window).innerWidth()) / 2
			)}px)`
		);
	};
	fixStickyNav();

	//Resize Listener
	$(window).on(
		`${horizontalResizeEvent}`,
		debounce(() => {
			checkFiltersContainer();
			fixFilterBold();
			fixStickyNav();
			topOfContainer = $container.offset().top;
			ScrollTrigger.refresh();
		})
	);

	const showCurrentFilter = (tag, dontScrollOrApplyToHistory = false) => {
		//Set the new active filter
		activeFilterTag = tag;
		$dropdown.val(tag);

		//Set the url, remove hash if "all" is selected
		if (!dontScrollOrApplyToHistory) {
			let newHash = activeFilterTag === 'all' ? '' : `#${activeFilterTag}`;
			const newUrl = `${window.location.origin}${window.location.pathname}${window.location.search}${newHash}`;
			window.history.replaceState({ id: $container.attr('id') }, '', newUrl);
		}

		//Find the right section and show or hide
		if (tag === 'all') {
			$filterSections.show();
		} else {
			$container
				.find(`${selectors.filter}[data-filter-tag='${tag}']`)
				.addClass(activeFilterClass);
			$filterSections.hide();
			$container.find(`${selectors.filterSection}[data-filter-tag='${tag}']`).show();
		}
		$container.trigger('filterChange', tag);
		ScrollTrigger.refresh();
	};
	showCurrentFilter(activeFilterTag, usingDefaultFilter);

	//Filter Listener
	$filters.each((index, filter) => {
		const $filter = $(filter);
		//Also add filter Listener
		$filter.on('click', e => {
			//change to active
			$container
				.find(`${selectors.filter}.${activeFilterClass}`)
				.removeClass(activeFilterClass);
			$filter.addClass(activeFilterClass);

			const currentTag = $filter.data('filterTag');
			showCurrentFilter(currentTag);
		});
	});

	//Dropdown Filter Listener
	$dropdown.on('change', e => {
		const tag = $dropdown.val();
		showCurrentFilter(tag);
		//jump back to top of the component
		$(window).scrollTop(topOfContainer - mobileNavHeight);
	});

	//Display Button Listeners
	$columnsButton.on('click', function (e) {
		if (!$columnsButton.hasClass(activeDisplayButtonClass)) {
			$columnsButton.addClass(activeDisplayButtonClass);
			$listButton.removeClass(activeDisplayButtonClass);
			$allItems
				.removeClass(`${listItemClass} ${listGridClasses} ${gridOffsetClasses}`)
				.addClass(`${columnsItemClass} ${columnsGridClasses}`);

			//needs to be based on the current section not all items
			$filterSections.each((sectionIndex, section) => {
				$(section)
					.find(selectors.item)
					.each((index, item) => {
						if (index % 2 === 0) {
							$(item).addClass(gridOffsetClasses);
						}
					});
			});
			ScrollTrigger.refresh();
			$container.trigger('layoutChange', 'columns');
		}
	});

	$listButton.on('click', function (e) {
		if (!$listButton.hasClass(activeDisplayButtonClass)) {
			$listButton.addClass(activeDisplayButtonClass);
			$columnsButton.removeClass(activeDisplayButtonClass);
			$allItems
				.removeClass(`${columnsItemClass} ${columnsGridClasses}`)
				.addClass(`${listItemClass} ${listGridClasses} ${gridOffsetClasses}`);
			ScrollTrigger.refresh();
			$container.trigger('layoutChange', 'list');
		}
	});

	const scrollToFCL = () => {
		const bp = getBreakpoint();
		const navOffset =
			bp === 'small' || bp === 'medium' ? mobileNavHeight : desktopNavHeight + 20;
		hashParam = window.location.hash.replace('#', '');
		if (filterValues.includes(hashParam)) {
			$('html, body').animate(
				{
					scrollTop: topOfContainer - navOffset,
				},
				500
			);
		}
	};
	scrollToFCL();
	$(window).on('hashchange', scrollToFCL);
};

export default function initFilteredColumnsList() {
	$(selectors.base).each((index, fcl) => {
		inittabBar($(fcl));
	});
}
